import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { ChatCircleDots, Spinner, Trash, CheckCircle, ArrowCounterClockwise, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (d) => d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtPref = (d) => d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "No preference";

export default function AdminDemoRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/demo-requests").then(({ data }) => { setRows(data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  if (user.role !== "super_admin") {
    return <div className="p-8 text-zinc-500" data-testid="demo-admin-denied">Super admin access only.</div>;
  }

  const setStatus = async (r, status) => {
    try {
      await api.post(`/admin/demo-requests/${r.id}/status`, { status });
      setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, status } : x));
      toast.success(status === "contacted" ? "Marked as contacted" : "Reopened");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/demo-requests/${delTarget.id}`);
      setRows((rs) => rs.filter((x) => x.id !== delTarget.id));
      setDelTarget(null);
      toast.success("Demo request deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const newCount = rows.filter((r) => r.status !== "contacted").length;

  return (
    <div className="space-y-4" data-testid="admin-demo-requests-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Demo Requests</h1>
          <p className="text-zinc-500 text-sm mt-1">{rows.length} total · {newCount} new</p>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead>Received</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Use Case</TableHead>
              <TableHead>Team</TableHead>
              <TableHead>Preferred Time</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-16"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-16 text-zinc-400"><ChatCircleDots size={32} className="mx-auto mb-2 text-zinc-300" />No demo requests yet.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className={r.status === "contacted" ? "opacity-70" : ""} data-testid={`demo-row-${r.id}`}>
                <TableCell className="text-xs text-zinc-500 whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-sm font-medium">{r.first_name} {r.last_name}</TableCell>
                <TableCell className="text-sm">{r.company}</TableCell>
                <TableCell className="text-sm">
                  <a href={`mailto:${r.work_email}`} className="flex items-center gap-1.5 text-emerald-700 hover:underline" data-testid={`demo-email-${r.id}`}><EnvelopeSimple size={14} /> {r.work_email}</a>
                  {r.phone && <div className="text-xs text-zinc-400 mt-0.5">{r.phone}</div>}
                </TableCell>
                <TableCell className="text-sm">{r.use_case || "—"}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{r.team_size || "—"}</TableCell>
                <TableCell className="text-xs text-zinc-500 whitespace-nowrap">{fmtPref(r.preferred_time)}</TableCell>
                <TableCell>
                  {r.status === "contacted"
                    ? <Badge className="bg-zinc-200 text-zinc-700 hover:bg-zinc-200">Contacted</Badge>
                    : <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">New</Badge>}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end">
                    {r.status === "contacted" ? (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500" title="Reopen" onClick={() => setStatus(r, "new")} data-testid={`demo-reopen-${r.id}`}><ArrowCounterClockwise size={16} /></Button>
                    ) : (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Mark contacted" onClick={() => setStatus(r, "contacted")} data-testid={`demo-contacted-${r.id}`}><CheckCircle size={18} weight="fill" /></Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="Delete" onClick={() => setDelTarget(r)} data-testid={`demo-delete-${r.id}`}><Trash size={16} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete demo request?</AlertDialogTitle><AlertDialogDescription>This permanently removes the request from {delTarget?.company || "this lead"}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="demo-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="demo-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
